// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: teamMembers/$memberId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Stores wallet addresses that have team member access in the Danger Room chat. Admin or founder can add/remove team wallets. The $memberId is the wallet address itself for easy get() lookups.
 */

export interface TeamMembersRequest {
  walletAddress: string;
  addedBy: string;
  addedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface TeamMembersResponse {
  walletAddress: string;
  addedBy: string;
  addedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a TeamMembers operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildTeamMembers(memberId: string, data?: TeamMembersRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `teamMembers/${memberId}`, document: doc };
}

/**
 * Admin or founder only. Creates a team member entry. Document ID ($memberId) must be the wallet address of the team member. All fields (walletAddress, addedBy, addedAt) are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setTeamMembers(memberId: string, data?: TeamMembersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`teamMembers/${memberId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting TeamMembers: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
   (Get Single Item)
 */
export async function getTeamMembers(memberId: string): Promise<TeamMembersResponse | null> {
  try {
    const result = await get(`teamMembers/${memberId}`);
    return result as TeamMembersResponse | null;
  } catch (error) {
    console.error(`Error getting TeamMembers: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single TeamMembers document. (
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
  )
 */
export function subscribeTeamMembers(callback: (data: TeamMembersResponse | null) => void, memberId: string): Promise<() => Promise<void>> {
  return subscribe(`teamMembers/${memberId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as TeamMembersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to TeamMembers at path '${`teamMembers/${memberId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many TeamMembers items from collection teamMembers
 
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
  
 */
export async function getManyTeamMembers(filter: string = ""): Promise<TeamMembersResponse[]> {
  try {
    const results = await get("teamMembers", { prompt: filter });
    return Array.isArray(results) ? results as TeamMembersResponse[] : [];
  } catch (error) {
    console.error(`Error getting TeamMembers collection at path 'teamMembers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in TeamMembers collection at teamMembers
 
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
  
 */
export function subscribeManyTeamMembers(callback: (data: TeamMembersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("teamMembers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TeamMembersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to TeamMembers collection at path '${"teamMembers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all TeamMembers items from collection teamMembers
 
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
  
 */
export async function getAllTeamMembers(filter: string = ""): Promise<TeamMembersResponse[]> {
  try {
    const results = await get("teamMembers", { prompt: filter });
    return Array.isArray(results) ? results as TeamMembersResponse[] : [];
  } catch (error) {
    console.error(`Error getting TeamMembers collection at path 'teamMembers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in TeamMembers collection at teamMembers
 
  Read Operation Details: Anyone can read team member entries. Used to check if a wallet address is a team member for Danger Room moderation access.
  
 */
export function subscribeAllTeamMembers(callback: (data: TeamMembersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("teamMembers", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TeamMembersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to TeamMembers collection at path '${"teamMembers"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count TeamMembers items in collection teamMembers.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countTeamMembers(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("teamMembers", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting TeamMembers at path 'teamMembers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on TeamMembers items in collection teamMembers.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateTeamMembers(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("teamMembers", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating TeamMembers at path 'teamMembers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Removes a wallet from the team member list, revoking their moderation access.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteTeamMembers(memberId: string): Promise<boolean> {
  try {
    const succeeded = await set(`teamMembers/${memberId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting TeamMembers: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for TeamMembers for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteTeamMembers(memberId: string): DocumentOperation {
  return { path: `teamMembers/${memberId}`, document: null };
}

